import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
} from "recharts";
import { ArrowLeft, ArrowUpRight, HeartPulse, Cpu, Palette, Scale, TrendingUp } from "lucide-react";
import api from "../lib/api";
import Navbar from "../components/Navbar";

const ICONS = {
  healthcare: HeartPulse,
  technology: Cpu,
  arts_culture: Palette,
  law_governance: Scale,
  commerce_economy: TrendingUp,
};
const ACCENTS = {
  healthcare: "#10B981",
  technology: "#06B6D4",
  arts_culture: "#F59E0B",
  law_governance: "#4F46E5",
  commerce_economy: "#64748B",
};

export default function EntityDetail() {
  const { type, slug } = useParams();
  const [item, setItem] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/indices/${type}/${slug}`);
        setItem(data);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      }
    })();
  }, [type, slug]);

  if (error)
    return (
      <div className="App">
        <Navbar />
        <p className="p-12 text-red-600 font-body" data-testid="entity-error">{error}</p>
      </div>
    );
  if (!item)
    return (
      <div className="App">
        <Navbar />
        <p className="p-12 font-ui text-xs uppercase tracking-widest text-[var(--es-ink-soft)]">
          Loading…
        </p>
      </div>
    );

  const radarData = item.domain_scores.map((d) => ({
    domain: d.label,
    score: d.score,
    fullMark: 10,
  }));

  return (
    <div className="App" data-testid="entity-detail-page">
      <Navbar />
      <section className="border-b border-[var(--es-line)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
          <Link
            to={`/indices/${type}`}
            className="inline-flex items-center gap-2 font-ui text-xs uppercase tracking-widest text-[var(--es-ink-soft)] hover:text-[var(--es-blue)]"
            data-testid="entity-back-link"
          >
            <ArrowLeft size={14} strokeWidth={1.6} /> Back to {item.type_label}
          </Link>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mt-10 items-end">
            <div className="md:col-span-7">
              <p className="font-ui text-xs tracking-[0.28em] uppercase text-[var(--es-blue)]">
                {item.type_label} · Rank {String(item.rank).padStart(2, "0")} of {item.total}
              </p>
              <h1 className="font-display text-5xl sm:text-6xl md:text-7xl text-[var(--es-ink)] tracking-tight mt-3 leading-[1.02]">
                {item.name}
              </h1>
              <p className="font-body text-[var(--es-ink-soft)] mt-4 text-lg max-w-2xl leading-relaxed">
                {item.summary}
              </p>
            </div>
            <div className="md:col-span-5 md:text-right">
              <p className="font-ui text-xs uppercase tracking-widest text-[var(--es-ink-soft)]">
                Erudition Score
              </p>
              <p className="font-display text-7xl md:text-8xl text-[var(--es-blue)] leading-none">
                {item.overall}
                <span className="text-[var(--es-ink-soft)] text-3xl"> / 10</span>
              </p>
              <p className="font-display text-2xl text-[var(--es-ink)] mt-2">{item.tier?.label}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-[var(--es-line)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 border border-[var(--es-line)] p-6 md:p-10 bg-white">
            <p className="font-ui text-[11px] tracking-widest uppercase text-[var(--es-ink-soft)] mb-6">
              Across the five pillars
            </p>
            <div className="h-[420px]">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData} outerRadius="78%">
                  <PolarGrid stroke="#E2E8F0" />
                  <PolarAngleAxis
                    dataKey="domain"
                    tick={{ fill: "#4B5563", fontSize: 12, fontFamily: "Outfit" }}
                  />
                  <PolarRadiusAxis
                    angle={90}
                    domain={[0, 10]}
                    tick={{ fill: "#9CA3AF", fontSize: 10 }}
                    tickCount={6}
                    stroke="#E2E8F0"
                  />
                  <Radar
                    dataKey="score"
                    stroke="#4267B4"
                    fill="#4267B4"
                    fillOpacity={0.18}
                    strokeWidth={2}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="lg:col-span-5 grid grid-cols-1 gap-3">
            {item.domain_scores.map((d) => {
              const Icon = ICONS[d.key];
              const accent = ACCENTS[d.key];
              return (
                <div
                  key={d.key}
                  className="border border-[var(--es-line)] bg-white p-5 flex items-center gap-5"
                  data-testid={`entity-domain-${d.key}`}
                >
                  <div
                    className="w-12 h-12 flex items-center justify-center shrink-0"
                    style={{ background: accent + "1A", color: accent }}
                  >
                    <Icon size={22} strokeWidth={1.5} />
                  </div>
                  <div className="flex-1">
                    <p className="font-display text-xl text-[var(--es-ink)]">{d.label}</p>
                    <div className="mt-1 h-[3px] bg-[var(--es-line)] relative">
                      <div
                        className="absolute left-0 top-0 h-full"
                        style={{ width: `${d.score * 10}%`, background: accent }}
                      />
                    </div>
                  </div>
                  <p className="font-display text-3xl text-[var(--es-blue)] w-16 text-right">
                    {d.score.toFixed(1)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {item.sources?.length > 0 && (
        <section className="border-b border-[var(--es-line)] bg-[var(--es-bg-soft)]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
            <p className="font-ui text-[11px] tracking-widest uppercase text-[var(--es-ink-soft)]">
              Sources
            </p>
            <p className="font-display text-xl text-[var(--es-ink)] mt-2">
              {item.sources.join(" · ")}
            </p>
            <p className="font-body text-xs italic text-[var(--es-ink-soft)] mt-4 max-w-2xl">
              Composite scores are illustrative — compiled from the indices above and editorial
              judgement, not a peer-reviewed empirical model.
            </p>
          </div>
        </section>
      )}

      <section>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 flex flex-wrap gap-4">
          <Link
            to="/quiz"
            className="bg-[var(--es-blue)] text-white px-8 py-4 hover:bg-[var(--es-blue-hover)] transition-colors uppercase tracking-wider text-sm font-semibold font-ui inline-flex items-center gap-2"
            data-testid="entity-cta-take-quiz"
          >
            Compute your own Erudition Score <ArrowUpRight size={14} strokeWidth={1.6} />
          </Link>
          <Link
            to={`/indices/${type}`}
            className="border border-[var(--es-blue)] text-[var(--es-blue)] px-8 py-4 hover:bg-[var(--es-blue-soft)] transition-colors uppercase tracking-wider text-sm font-semibold font-ui"
          >
            See the full index
          </Link>
        </div>
      </section>
    </div>
  );
}
